import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import { checkIsGuestAccount } from '@/util/guest-util';
import { namespace } from 'vuex-class';
import { onErrorImageAvatar } from '@/util/common-util';
import UserMyPageService from '@/service/userMyPage.service';
import { IUserModel, UserModel } from '@/shared/model/users.model';
import { removeUnUsedSpace } from '@/util/string.util';
import PhoneValid from '@/v2/my-page/modal/phone-valid/phone-valid.vue';
import ListLoading from '@/entities/resource-management/list-loading/list-loading.vue';

const appStoreState = namespace('appStore');
const MAX_SIZE_AVATAR = 3;

@Component({
  computed: {},
  methods: { checkIsGuestAccount, onErrorImageAvatar },
  components: {
    ListLoading,
    'modal-phone-valid': PhoneValid,
  },
})
export default class MyPageEdit extends Vue {
  openEditPhone = false;

  @Inject('userMyPageService')
  private userMyPageService: () => UserMyPageService;

  private fileAvatar;

  @appStoreState.Mutation
  public setIsOpenFirstStepDeleteAccountModal!: (payload: any) => void;

  @appStoreState.Getter
  getIsOpenFirstStepDeleteAccountModal!: boolean;

  @appStoreState.Mutation
  public setCurrentUserImageUri!: (payload: any) => void;

  @appStoreState.Getter
  getColorTheme!: string;

  isLoadingUpdateUser = false;
  selectedColorTheme = this.getColorTheme || 'light';
  user: IUserModel = new UserModel(this.selectedColorTheme);
  isValidLastName = true;
  isChangeInfo = false;
  currentUsername = '';

  roueToMyPage() {
    this.$router.push('/my-page');
  }

  mounted() {
    this.getUser();
  }

  openPopupUpload() {
    const fileUpload = document.getElementById('fileUpload') as HTMLInputElement;
    fileUpload.click();
  }

  saveMoreInfo(data) {
    if (this.user.email) {
      this.user.phoneNumber = data.phoneNumber;
      this.user.phoneCountry = data.countryCode;
    } else {
      this.user.email = data.email;
    }
    this.updateUser();
  }

  waitingCallApi = false;
  getUser() {
    this.waitingCallApi = true;
    this.userMyPageService()
      .callApiGetUser()
      .then(response => {
        if (response) {
          if (response?.data) {
            this.user = response?.data;
            this.currentUsername = this.user.lastName;
            this.isChangeInfo = this.user.email !== null && this.user.phoneNumber !== null;
            if (!this.user.imageUrl) {
              this.user.imageUrl = 'content/images/avatar.png';
            }
          }
        }
      })
      .finally(() => {
        this.waitingCallApi = false;
      });
  }

  openModal() {
    this.openEditPhone = true;
  }

  closeModal() {
    this.openEditPhone = false;
  }

  goTo(destination: string) {
    this.$router.push(destination);
  }

  @Watch('getColorTheme')
  onColorThemeChange() {
    if (this.getColorTheme) {
      this.selectedColorTheme = this.getColorTheme;
    } else {
      this.selectedColorTheme = localStorage.getItem('theme') || 'light';
    }
  }

  blobToBase64(blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  uploadAvatar(e) {
    const file = e.target?.files?.[0];
    if (file && file?.size && file?.size > MAX_SIZE_AVATAR * 1024 * 1024) {
      this.showResponseMessage(`${this.$t('toast.maxImageSize')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
      return;
    }

    this.blobToBase64(file).then((res: string) => {
      this.user.imageUrl = res;
    });

    this.isChangeInfo = false;
    this.fileAvatar = file;

    if (e?.target?.value) {
      e.target.value = '';
    }
  }

  public createUser(): IUserModel {
    return {
      ...new UserModel(),
      id: this.user.id,
      login: this.user.login,
      password: this.user.password,
      firstName: this.user.firstName,
      lastName: removeUnUsedSpace(this.user.lastName),
      email: this.user.email,
      phoneCountry: this.user.phoneCountry,
      phoneNumber: this.user.phoneNumber,
      imageUrl: '',
      activated: this.user.activated,
      langKey: this.user?.langKey || this.$i18n.locale || localStorage.getItem('currentLanguage') || 'en',
      activationKey: this.user.activationKey,
      resetKey: this.user.resetKey,
      createdBy: this.user.createdBy,
      lastModifiedBy: this.user.lastModifiedBy,
      termOfService: this.user.termOfService,
      receiveNews: this.user.receiveNews,
      showOnboardDialog: this.user.showOnboardDialog,
      colorTheme: this.user.colorTheme,
    };
  }

  refactorLastNameInput() {
    if (!this.user.lastName) return;

    const tempName = this.user.lastName?.trim().replace(' ', '').replace(/\s+/g, '');
    this.isChangeInfo =
      this.user.email !== null && this.user.phoneNumber !== null && this.currentUsername.trim() === this.user.lastName.trim();

    this.isValidLastName = !(!tempName || (tempName && tempName.length === 0));
    if (this.user.lastName?.length > 50) {
      this.user.lastName = this.user.lastName.substring(0, 50);
    }
  }

  updateUser() {
    if (!this.isValidLastName || !this.user.lastName) {
      return;
    }
    //
    // if (this.phoneNumberModel && this.phoneNumberModel.length > 0 && (this.user.phoneCountry === null || !this.user.phoneCountry)) {
    //   return;
    // }
    //
    // if (
    //   this.user?.phoneCountry !== undefined &&
    //   this.user?.phoneCountry !== null &&
    //   this.user?.phoneCountry?.trim()?.length > 0 &&
    //   (!this.phoneNumberModel || this.phoneNumberModel?.length === 0)
    // ) {
    //   return;
    // }

    this.isLoadingUpdateUser = true;

    const formData = new FormData();
    formData.append('file', this.fileAvatar);
    const userUpdate = this.createUser();
    formData.append('user', JSON.stringify(userUpdate));

    this.userMyPageService()
      .updateUser(formData)
      .then(res => {
        this.user = res.data;
        this.setCurrentUserImageUri(this.user.imageUrl);
        this.$store.commit('setUserIdentity', res?.data);

        this.showResponseMessage(`${this.$t('toast.update.success')}`, `${this.$t('toast.title.success')}`, 'success', 5000);

        this.isLoadingUpdateUser = false;
      })
      .catch(err => {
        if (err.response?.data?.title) {
          switch (err.response?.data?.title) {
            case 'Email is already in use!':
              this.showResponseMessage(`${this.$t('toast.emailInUse')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
              break;
            case 'Phone Number is already in use!':
              this.showResponseMessage(`${this.$t('toast.phoneInUse')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
              break;
            case 'Login name already used!':
              this.showResponseMessage(`${this.$t('toast.idInUse')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
              break;
            default:
              this.showResponseMessage(`${this.$t('toast.update.fail')}`, `${this.$t('toast.title.error')}`, 'danger', 5000);
              break;
          }
        }

        this.isLoadingUpdateUser = false;
      });
  }

  showResponseMessage(mess, title, variant, hideDelay) {
    this.$bvToast.toast(mess, {
      toaster: 'b-toaster-bottom-right',
      title: title,
      variant: variant,
      solid: true,
      autoHideDelay: hideDelay,
    });
  }
}
